"use client";

import React, { useState } from 'react';
import { Code2, Eye, EyeOff, Copy, CheckCircle, AlertTriangle, Loader2, ExternalLink, Zap,  } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { useAppToast } from '@/lib/use-app-toast';
import { auditLog } from '@/lib/supabase/audit-logger';

interface APIConfig {
  key: string;
  label: string;
  labelBn: string;
  value: string;
  description: string;
  status: 'connected' | 'error' | 'unconfigured';
  lastTested?: string;
  placeholder: string;
  docs: string;
}

export default function APISettings() {
  const { language } = useLanguage();
  const { success, error: toastError } = useAppToast();
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [isTesting, setIsTesting] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [configs, setConfigs] = useState<APIConfig[]>([
    {
      key: 'gemini',
      label: t(language, 'settings_api_gemini'),
      labelBn: 'জেমিনি এপিআই কী',
      value: 'AIzaSyB8K2mN9xL4vR7qP1wE3tY6uI0oA5sD2fG',
      description: 'Powers the AI Threat Assistant. Gemini 1.5 Pro model.',
      status: 'connected',
      lastTested: '2026-03-17 03:45 BDT',
      placeholder: 'AIzaSy...',
      docs: 'https://ai.google.dev',
    },
    {
      key: 'supabase_url',
      label: 'Supabase Project URL',
      labelBn: 'সুপাবেস প্রজেক্ট ইউআরএল',
      value: 'https://xyzabcdef.supabase.co',
      description: 'Authentication, user management, and audit log storage.',
      status: 'connected',
      lastTested: '2026-03-17 04:00 BDT',
      placeholder: 'https://[project-id].supabase.co',
      docs: 'https://supabase.com/docs',
    },
    {
      key: 'supabase_anon',
      label: 'Supabase Anon Key',
      labelBn: 'সুপাবেস অ্যানোন কী',
      value: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSJ9.abc',
      description: 'Public anonymous key for client-side Supabase operations.',
      status: 'connected',
      lastTested: '2026-03-17 04:00 BDT',
      placeholder: 'eyJhbGci...',
      docs: 'https://supabase.com/docs/guides/api',
    },
    {
      key: 'cirt_webhook',
      label: 'CIRT-BD Webhook URL',
      labelBn: 'সিআইআরটি-বিডি ওয়েবহুক ইউআরএল',
      value: '',
      description: 'Auto-report critical incidents to BTRC CIRT-BD under §7 obligation.',
      status: 'unconfigured',
      placeholder: 'https://cirt.btrc.gov.bd/webhook/...',
      docs: 'https://cirt.btrc.gov.bd',
    },
    {
      key: 'siem_endpoint',
      label: 'SIEM Integration Endpoint',
      labelBn: 'এসআইইএম ইন্টিগ্রেশন এন্ডপয়েন্ট',
      value: 'https://siem.btrc.internal:9200',
      description: 'Elasticsearch/OpenSearch endpoint for log forwarding.',
      status: 'error',
      lastTested: '2026-03-17 02:30 BDT',
      placeholder: 'https://siem.internal:9200',
      docs: 'https://opensearch.org/docs',
    },
  ]);

  const toggleShow = (key: string) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    success(`${label} copied to clipboard`);
  };

  const handleTest = async (key: string) => {
    setIsTesting(prev => ({ ...prev, [key]: true }));
    // Backend integration: await testAPIConnection(key, configs.find(c => c.key === key)?.value)
    await new Promise(r => setTimeout(r, 1500));
    setIsTesting(prev => ({ ...prev, [key]: false }));

    if (key === 'siem_endpoint') {
      await auditLog.apiKeyTested(key, 'failed');
      toastError('Connection failed — SIEM endpoint unreachable', 'Check network connectivity and firewall rules.');
    } else if (key === 'cirt_webhook') {
      await auditLog.apiKeyTested(key, 'failed');
      toastError('Webhook URL not configured');
    } else {
      setConfigs(prev => prev.map(c => c.key === key
        ? { ...c, status: 'connected', lastTested: new Date().toLocaleString('en-BD') }
        : c
      ));
      await auditLog.apiKeyTested(key, 'success');
      success(`${key} connection verified`);
    }
  };

  const handleValueChange = (key: string, value: string) => {
    setConfigs(prev => prev.map(c => c.key === key ? { ...c, value } : c));
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    // Backend integration: await supabase.from('platform_config').upsert(configs.map(c => ({ key: c.key, value: c.value })))
    await new Promise(r => setTimeout(r, 1200));
    await Promise.all(configs.map(c => auditLog.apiKeyUpdated(c.label)));
    setIsSaving(false);
    success('API configuration saved', 'All integration settings updated securely.');
  };

  const maskValue = (value: string) => {
    if (!value) return '';
    if (value.length <= 8) return '••••••••';
    return value.slice(0, 8) + '••••••••••••••••' + value.slice(-4);
  };

  const statusConfig: Record<string, { icon: React.ReactNode; color: string; bg: string; border: string; label: string }> = {
    connected: {
      icon: <CheckCircle size={11} />,
      color: 'text-cyber-green',
      bg: 'bg-green-500/10',
      border: 'border-green-500/25',
      label: 'Connected',
    },
    error: {
      icon: <AlertTriangle size={11} />,
      color: 'text-cyber-red',
      bg: 'bg-red-500/10',
      border: 'border-red-500/25',
      label: 'Error',
    },
    unconfigured: {
      icon: <AlertTriangle size={11} />,
      color: 'text-cyber-muted',
      bg: 'bg-white/5',
      border: 'border-cyber-border',
      label: 'Not Configured',
    },
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="cyber-card p-4 flex items-center gap-3 border-cyber-blue/20">
        <div className="w-9 h-9 rounded-xl bg-cyber-blue/15 border border-cyber-blue/25 flex items-center justify-center">
          <Code2 size={16} className="text-cyber-blue" />
        </div>
        <div>
          <p className="text-sm font-semibold text-white">API & Integrations</p>
          <p className="text-[11px] text-cyber-muted font-mono">
            {configs.filter(c => c.status === 'connected').length}/{configs.length} integrations active
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {configs.filter(c => c.status === 'error').length > 0 && (
            <span className="cyber-badge-critical text-[10px]">
              {configs.filter(c => c.status === 'error').length} errors
            </span>
          )}
          {configs.filter(c => c.status === 'unconfigured').length > 0 && (
            <span className="cyber-badge-medium text-[10px]">
              {configs.filter(c => c.status === 'unconfigured').length} unconfigured
            </span>
          )}
        </div>
      </div>

      {/* API Config Cards */}
      <div className="space-y-4">
        {configs.map((config) => {
          const status = statusConfig[config.status];
          const isShowing = showKeys[config.key];
          const testing = isTesting[config.key];

          return (
            <div
              key={config.key}
              className={`cyber-card p-5 ${
                config.status === 'error' ? 'border-red-500/20' :
                config.status === 'unconfigured'? 'border-cyber-border' : 'border-cyber-border hover:border-cyber-blue/15'
              } transition-colors`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
                      {language === 'bn' ? config.labelBn : config.label}
                    </h4>
                    <a
                      href={config.docs}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyber-subtle hover:text-cyber-blue transition-colors"
                    >
                      <ExternalLink size={11} />
                    </a>
                  </div>
                  <p className="text-[11px] text-cyber-muted mt-0.5">{config.description}</p>
                </div>

                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-mono font-medium shrink-0 ${status.bg} ${status.border} ${status.color}`}>
                  {status.icon}
                  {status.label}
                </div>
              </div>

              {/* Input */}
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type={isShowing ? 'text' : 'password'}
                    value={config.value}
                    onChange={(e) => handleValueChange(config.key, e.target.value)}
                    placeholder={config.placeholder}
                    className={`w-full bg-[#131620] border rounded-lg px-3 py-2 text-[13px] font-mono outline-none transition-colors pr-10 ${
                      config.status === 'error' ?'border-red-500/40 text-cyber-red focus:border-red-500/60' :'border-cyber-border text-cyber-text focus:border-cyber-blue/40'
                    } placeholder-cyber-subtle`}
                  />
                  <button
                    type="button"
                    onClick={() => toggleShow(config.key)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-cyber-muted hover:text-cyber-text transition-colors"
                  >
                    {isShowing ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>

                <button
                  onClick={() => handleCopy(config.value, config.label)}
                  disabled={!config.value}
                  title="Copy to clipboard"
                  className="w-9 h-9 rounded-lg bg-white/5 border border-cyber-border hover:bg-white/10 flex items-center justify-center transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed text-cyber-muted hover:text-cyber-text"
                >
                  <Copy size={13} />
                </button>

                <button
                  onClick={() => handleTest(config.key)}
                  disabled={testing || !config.value}
                  title="Test connection"
                  className={`
                    flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium
                    transition-all duration-150 active:scale-95 whitespace-nowrap
                    disabled:opacity-40 disabled:cursor-not-allowed
                    ${config.status === 'error' ?'bg-red-500/15 border border-red-500/25 text-cyber-red hover:bg-red-500/25' :'bg-white/5 border border-cyber-border text-cyber-muted hover:bg-cyber-blue/10 hover:border-cyber-blue/25 hover:text-cyber-blue'
                    }
                  `}
                >
                  {testing
                    ? <><Loader2 size={12} className="animate-spin" />Testing...</>
                    : <><Zap size={12} />Test</>
                  }
                </button>
              </div>

              {/* Last tested */}
              {config.lastTested && (
                <p className="text-[10px] text-cyber-muted font-mono mt-2">
                  Last tested: {config.lastTested}
                </p>
              )}

              {config.status === 'error' && (
                <div className="mt-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-[11px] text-cyber-red font-mono">
                    ⚠️ Connection failed — verify endpoint URL and network access from this server
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save All */}
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-cyber-muted font-mono">
          🔒 All API keys are stored encrypted at rest (AES-256)
        </p>
        <button
          onClick={handleSaveAll}
          disabled={isSaving}
          className={`
            flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium
            transition-all duration-150 active:scale-95
            ${isSaving
              ? 'bg-cyber-surface border border-cyber-border text-cyber-subtle cursor-not-allowed' :'bg-cyber-blue hover:bg-cyber-blue/80 text-white shadow-[0_0_15px_rgba(0,163,255,0.25)]'
            }
          `}
          style={{ minWidth: '140px' }}
        >
          {isSaving ? (
            <><Loader2 size={14} className="animate-spin" /><span>Saving...</span></>
          ) : (
            <><Code2 size={14} /><span className={language === 'bn' ? 'font-bangla' : ''}>{t(language, 'settings_save')}</span></>
          )}
        </button>
      </div>
    </div>
  );
}